import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { Group } from '../../../models/group.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../../models/member.school.model';
import { MembershipStatus } from '../../../models/membership.status';
import { UtilsService } from '../../../services/utils.service';
import { environment } from '../../../../environments/environment';
import * as memberActions from '../../../state/actions/member.school.actions';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { DlgPlanLimitComponent } from '../../components/dialogs/dlg-plan-limit/dlg-plan-limit.component';
import { GroupFeatureAccessService } from '../../../services/group-feature-access.service';

@Component({
  selector: 'app-dlg-assign-roles',
  standalone: true,
  imports: [
    MatFormFieldModule,
    BtnActionComponent,
    AsyncPipe,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    BtnActionComponent,
  ],
  templateUrl: './dlg-assign-roles.component.html',
  styleUrl: './dlg-assign-roles.component.css',
})
export class DlgAssignRolesComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<DlgAssignRolesComponent>);
  private dialog: MatDialog = inject(MatDialog);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  private actions$: Actions = inject(Actions);
  private utilsService = inject(UtilsService);
  private featureAccessService = inject(GroupFeatureAccessService);
  logger: NGXLogger = inject(NGXLogger);

  store: Store<AppState> = inject(Store<AppState>);

  title: string = 'Update Member Role(s)';
  description: string = 'Select an option to proceed';
  action: string = 'Assign Roles';
  options: SchoolMemberType[] = Object.values(SchoolMemberType);
  selectedOption: string[];
  member: SchoolMember = this.data.member;

  working$: Observable<boolean> = of(false);
  exit$: Subject<void> = new Subject();

  group: Group | undefined;
  
  seatsLimit: number = 25;
  isSubscribed: boolean = false;

  constructor() {
    this.title = this.data.title ?? this.title;
    this.description = this.data.description;
    this.action = this.data.action;
    this.selectedOption = Object.assign([], this.data.member.memberType ?? []);
  }

  ngOnInit(): void {
    if (this.data.groupId) {
      const defaultFreeSeats = environment.STRIPE_SCHOOL_FREE_SEATS || 25;
      this.store
        .select(groupSelector.getGroupMetadataMap)
        .pipe(takeUntil(this.exit$))
        .subscribe((metadataMap) => {
          const metadata = metadataMap ? metadataMap[this.data.groupId] : null;
          if (metadata) {
            this.seatsLimit = metadata.subscribedSeatsTier ?? defaultFreeSeats;
            this.isSubscribed = this.seatsLimit > defaultFreeSeats;
          } else {
            this.seatsLimit = defaultFreeSeats;
            this.isSubscribed = false;
          }
        });
    }

    this.actions$
      .pipe(ofType(memberActions.update), takeUntil(this.exit$))
      .subscribe(({ id, currMember, member }) => {
        this.working$ = of(true);
      });

    // Subscribe to loadUserSuccess action
    this.actions$
      .pipe(ofType(memberActions.updateSuccess), takeUntil(this.exit$))
      .subscribe(() => {
        this.logger.debug('Member updated successfully');
        this.working$ = of(false);
        this.dialogRef.close(true);
      });

    // Subscribe to updateFailure action (optional, for error handling)
    this.actions$
      .pipe(ofType(memberActions.updateFailure), takeUntil(this.exit$))
      .subscribe(({ error }) => {
        this.logger.debug('Member update failed, error: ', error);
        this.utilsService.onShowAlert(error as AppExceptionModel, false);
        this.working$ = of(false);
      });

    this.store
      .select(groupSelector.getGroupById(this.data.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
      });
  }

  onChangeOption(event: any): void {
    const value: string = event.target.value;
    this.onTextSelection(value);
  }

  onTextSelection(value: string) {
    switch (value) {
      case SchoolMemberType.student.valueOf():
        try {
          const currentStudents =
            this.group?.students ?? this.group?.gradeStats?.['students'] ?? 0;
          const isAlreadyStudent = this.member.memberType?.includes(
            SchoolMemberType.student,
          );

          if (!isAlreadyStudent && currentStudents >= this.seatsLimit) {
            const limitDialogRef = DlgPlanLimitComponent.open(this.dialog, {
              groupId: this.data.groupId,
              seatsLimit: this.seatsLimit,
              isSubscribed: this.isSubscribed,
              actionType: 'assign_role',
            });
            limitDialogRef.afterClosed().subscribe((goToBilling) => {
              if (goToBilling) {
                this.dialogRef.close(false);
              }
            });
            return;
          }
        } catch (error) {
          this.utilsService.onShowAlert(error as AppExceptionModel, false);
          return;
        }
        this.selectedOption = [value];
        break;
      case SchoolMemberType.unassigned.valueOf():
        this.selectedOption = [value];
        break;
      default:
        if (this.selectedOption.includes(value)) {
          this.selectedOption = this.selectedOption.filter(
            (option) => option !== value,
          );
        } else {
          this.selectedOption.push(value);
        }

        if (value === SchoolMemberType.president.valueOf()) {
          var index = this.selectedOption.indexOf(
            SchoolMemberType.principal.valueOf(),
          );
          if (index > -1) {
            this.selectedOption.splice(index, 1);
          }
        }

        if (value === SchoolMemberType.principal.valueOf()) {
          var index = this.selectedOption.indexOf(
            SchoolMemberType.president.valueOf(),
          );
          if (index > -1) {
            this.selectedOption.splice(index, 1);
          }
        }

        if (this.selectedOption.length > 1) {
          var index = this.selectedOption.indexOf(
            SchoolMemberType.unassigned.valueOf(),
          );
          if (index > -1) {
            this.selectedOption.splice(index, 1);
          }

          index = this.selectedOption.indexOf(
            SchoolMemberType.student.valueOf(),
          );
          if (index > -1) {
            this.selectedOption.splice(index, 1);
          }
        }
        break;
    }
  }



  onCancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    this.logger.debug(`Selected Role(s): ${this.selectedOption}`);

    if (!this.featureAccessService.checkActionAllowed(true)) {
      this.onCancel();
      return;
    }

    if (!this.member) {
      this.utilsService.onShowAlert({
        title: 'Member Not Found',
        message: 'The member to update roles was not found.',
        code: 'member-not-found',
      });
      return;
    }

    if (this.selectedOption.length == 0) {
      this.onCancel();
      return;
    }
    return this.store.dispatch(
      memberActions.update({
        id: this.member?.id!,
        currMember: this.member,
        member: {
          ...this.member,
          memberType: this.selectedOption,
          status: this.selectedOption.includes(SchoolMemberType.unassigned)
            ? MembershipStatus.requested
            : MembershipStatus.active,
        } as SchoolMember,
      }),
    );
  }
}
